import { FormEvent, useMemo, useRef, useState } from 'react';

import { useMount } from 'react-use';

import { Stack, TextField, Typography } from '@mui/material';

import isEmpty from 'lodash/isEmpty';

import ArrowRightIcon from '@/components/Icons/ArrowRightIcon';
import { ApprovalStatus } from '@/graphql';
import useAsOperator from '@/hooks/useAsOperator';
import { useUpdateOrganizationMutation } from '@/hooks/useOrg/__generated__/mutations.generated';
import useUser from '@/hooks/useUser';
import { useSearchParams } from '@/routes';
import { useAnalytics } from '@/utils/analytics/segment/hooks';
import validate from '@/utils/validators/userName';
import { useSnackbar } from 'notistack';
import { useSetRecoilState } from 'recoil';

import { useExistingOrganizationQuery } from '../../__generated__/queries.generated';
import { onboardingStep } from '../../atoms';
import { useCompleteOnboarding, useExitOnboarding, useProfileData } from '../../hooks';
import { Label } from '../../styled';
import { OnboardingEvent, OnboardingStep } from '../../types';
import type { SignupArgs } from '../../types';
import { Body, Container, Description, Form, Header, NextButton, Title } from '../styled';

function getData(e: FormEvent<HTMLFormElement>) {
  return Object.fromEntries(new FormData(e.currentTarget).entries());
}

function ProfileStep() {
  const [searchParams] = useSearchParams();

  const joinExisting = useMemo(() => !!searchParams.get('joinExisting'), [searchParams]);
  const user = useUser();
  const isAbleToJoin = user.status === ApprovalStatus.EligibleToJoin;
  const analytics = useAnalytics();
  const isOperator = useAsOperator();
  const { enqueueSnackbar } = useSnackbar();
  const { data: existingOrganizationData } = useExistingOrganizationQuery({
    fetchPolicy: 'no-cache',
  });
  const [updateOrg] = useUpdateOrganizationMutation();
  const [loading, setLoading] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [, setProfileData] = useProfileData();
  const setStep = useSetRecoilState(onboardingStep);
  const completeOnboarding = useCompleteOnboarding();
  const exitOnboarding = useExitOnboarding();
  const formRef = useRef<HTMLFormElement>(null);

  async function nextStep() {
    if ((ENV_CONFIG.USE_ABRIDGED_ONBOARDING && !isAbleToJoin) || joinExisting || isOperator) {
      await completeOnboarding();
      exitOnboarding();

      return;
    }

    if (!joinExisting && isAbleToJoin && existingOrganizationData.existingOrganization) {
      setStep(OnboardingStep.JoinExisting);
      return;
    }

    setStep(OnboardingStep.CreateWorkspace);
  }

  useMount(() => {
    if (formRef.current) {
      // imitate the change event to set the initial state of the form
      handleChange(Object.fromEntries(new FormData(formRef.current).entries()));
    }
  });

  function handleChange({ firstName, lastName, workspaceName }: Partial<SignupArgs>) {
    const areRequiredFieldsEmpty = isEmpty(firstName?.trim()) || isEmpty(lastName?.trim());
    const areModelOnlyOnboardingFieldsEmpty =
      !joinExisting && !isAbleToJoin && isEmpty(workspaceName);

    const isDisabled =
      areRequiredFieldsEmpty ||
      (ENV_CONFIG.USE_ABRIDGED_ONBOARDING && areModelOnlyOnboardingFieldsEmpty);

    setDisabled(isDisabled);
  }

  async function handleSubmit({ firstName, lastName, workspaceName }: Partial<SignupArgs>) {
    analytics.track(OnboardingEvent.CompleteProfile, { email: user.email });

    try {
      validate(firstName, lastName);

      setProfileData({
        firstName,
        lastName,
        workspaceName,
      });

      if (ENV_CONFIG.USE_ABRIDGED_ONBOARDING && workspaceName) {
        setLoading(true);
        await updateOrg({ variables: { workspaceName } });

        setLoading(false);
        nextStep();
      } else {
        nextStep();
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  }

  const title =
    joinExisting && user.workspaceName
      ? `Welcome to ${user.workspaceName}!`
      : 'Welcome to Baseten!';

  const description = joinExisting
    ? 'Let’s start with a little information to help us get to know you.'
    : 'Create your profile to get started.';

  const nextButtonTitle =
    (ENV_CONFIG.USE_ABRIDGED_ONBOARDING || joinExisting) && !isAbleToJoin ? "Let's go" : 'Continue';

  return (
    <Container>
      <Header>
        <Title data-cy="onb-title">{title}</Title>
        <Description data-cy="onb-description">{description}</Description>
      </Header>
      <Form
        onChange={(ev: FormEvent<HTMLFormElement>) => handleChange(getData(ev))}
        onSubmit={(ev: FormEvent<HTMLFormElement>) => {
          ev.preventDefault();
          handleSubmit(getData(ev));
        }}
        ref={formRef}
        sx={{ width: '100%' }}
      >
        <Body>
          <Stack data-cy="onb-profile" direction="row" spacing={2} sx={{ width: '100%' }}>
            <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
              <Label id="first-name-lbl">First name</Label>
              <TextField
                inputProps={{
                  'data-cy': 'first-name',
                }}
                aria-labelledby="first-name-lbl"
                defaultValue={user.firstName}
                name="firstName"
                fullWidth
                autoFocus
              />
            </Stack>

            <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
              <Label id="last-name-lbl">Last name</Label>
              <TextField
                inputProps={{
                  'data-cy': 'last-name',
                }}
                aria-labelledby="last-name-lbl"
                defaultValue={user.lastName}
                name="lastName"
                fullWidth
              />
            </Stack>
          </Stack>

          {ENV_CONFIG.USE_ABRIDGED_ONBOARDING && !joinExisting && !isAbleToJoin && (
            <Stack direction="column" spacing={1} sx={{ pr: 12.5, width: '100%' }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                And what do you want to call your workspace?
              </Typography>

              <Typography variant="h5" id="onboarding-workspace-name">
                Workspace name
              </Typography>
              <TextField
                name="workspaceName"
                placeholder="e.g. Company name"
                aria-labelledby="onboarding-workspace-name"
                defaultValue={user.workspaceName}
              />
            </Stack>
          )}
          <Stack alignItems="end" width="100%">
            <NextButton
              disabled={disabled}
              data-cy="onb-submit"
              endIcon={<ArrowRightIcon />}
              loading={loading}
            >
              {nextButtonTitle}
            </NextButton>
          </Stack>
        </Body>
      </Form>
    </Container>
  );
}

export default ProfileStep;
